import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  ArrowRight,
  HandCoins,
  Link2,
  Palette,
  Plus,
  QrCode,
  TrendingUp,
} from "lucide-react";
import type { Paginated, UserDto } from "@gatiq/shared";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  Skeleton,
  StatCard,
  StatGrid,
} from "@/ui";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { useCan } from "@/auth/access";
import { useTenant } from "@/tenant/TenantProvider";

interface Counts {
  salesReps: number;
  counters: number;
  karigars: number;
}

interface BatchSummary {
  count: number;
  used: number;
  unscanned: number;
  outstanding: number;
  status: "active" | "void";
}

interface CodeTotals {
  activeBatches: number;
  issued: number;
  scanned: number;
  unscanned: number;
  outstanding: number;
}

const PREVIEW = 5;

/**
 * The org admin's landing screen: what needs doing (approvals), what is owed
 * (outstanding codes), and the way into everything else.
 *
 * Every figure comes from an endpoint another screen already uses — there is
 * no dashboard-only API. Each request settles on its own, so one failure
 * leaves a blank tile rather than an empty page.
 */
export function OrgAdminOverview() {
  const { orgSlug = "" } = useParams<{ orgSlug: string }>();
  const { payload } = useTenant();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Counts | null>(null);
  const [pending, setPending] = useState<Paginated<UserDto> | null>(null);
  const [codes, setCodes] = useState<CodeTotals | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { user } = useAuth();
  const can = useCan();
  // Anyone can be given this page (D-16); each figure on it still needs its own module.
  const see = {
    team: Boolean(can("team")),
    counters: Boolean(can("counters")),
    karigars: Boolean(can("karigars")),
    approvals: Boolean(can("approvals")),
    rewards: Boolean(can("rewards")),
  };
  const seeKey = JSON.stringify(see);

  useEffect(() => {
    let cancelled = false;
    const allowed = JSON.parse(seeKey) as typeof see;
    const only = <T,>(ok: boolean, get: () => Promise<T>): Promise<T> =>
      ok ? get() : Promise.reject(new Error("not granted"));

    async function load(): Promise<void> {
      // The sales team is everyone in the chain above the counters, not just sales reps.
      const team = ["sales_rep", "distributor", "sub_distributor"].map((role) =>
        only(allowed.team, () => api.get<Paginated<UserDto>>(`/org/sales-reps?role=${role}&page=1&pageSize=1`)),
      );
      const [counters, karigars, queue, batches] = await Promise.allSettled([
        only(allowed.counters, () => api.get<Paginated<UserDto>>("/org/counters?page=1&pageSize=1")),
        only(allowed.karigars, () => api.get<Paginated<UserDto>>("/org/karigars?page=1&pageSize=1")),
        only(allowed.approvals, () =>
          api.get<Paginated<UserDto>>(`/org/approvals?tab=pending&page=1&pageSize=${PREVIEW}`),
        ),
        only(allowed.rewards, () => api.get<{ items: BatchSummary[] }>("/org/rewards/batches")),
      ]);
      if (cancelled) return;

      const total = (r: PromiseSettledResult<Paginated<UserDto>>): number =>
        r.status === "fulfilled" ? r.value.total : 0;
      const teamTotal = (await Promise.allSettled(team)).reduce((n, r) => n + total(r), 0);
      setCounts({ salesReps: teamTotal, counters: total(counters), karigars: total(karigars) });

      if (queue.status === "fulfilled") setPending(queue.value);

      if (batches.status === "fulfilled") {
        const active = batches.value.items.filter((b) => b.status === "active");
        setCodes({
          activeBatches: active.length,
          issued: active.reduce((n, b) => n + b.count, 0),
          scanned: active.reduce((n, b) => n + b.used, 0),
            unscanned: active.reduce((n, b) => n + b.unscanned, 0),
          outstanding: active.reduce((n, b) => n + b.outstanding, 0),
        });
      }
      setLoaded(true);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [seeKey]);

  const base = `/${orgSlug}/admin`;
  const orgName = payload.orgName ?? "your organization";
  const pendingTotal = pending?.total ?? 0;
  const scannedShare = codes && codes.issued > 0 ? codes.scanned / codes.issued : 0;

  return (
    <Page>
      <PageHeader title="Overview" />

      <PageBody className="flex flex-col gap-5 pb-2 [&>*]:shrink-0">
        <section className="relative overflow-hidden rounded-lg bg-panel px-6 py-6 text-panel-text shadow-card sm:px-8">
          <div
            aria-hidden="true"
            className="gq-modules pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-[0.08] [mask-image:linear-gradient(to_left,black,transparent)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 right-24 size-72 rounded-full bg-brand/30 blur-[80px]"
          />
          <div className="relative flex flex-wrap items-end justify-between gap-5">
            <div className="min-w-0">
              <p className="text-sm text-panel-muted">{greeting()}</p>
              <h2 className="mt-1 font-title text-2xl leading-tight font-semibold tracking-[-0.02em] sm:text-[1.75rem]">
                {orgName}
              </h2>
              {see.approvals ? (
                <p className="mt-2 max-w-[52ch] text-sm text-panel-text/70">
                  {pendingTotal > 0
                    ? `${pendingTotal} ${pendingTotal === 1 ? "karigar is" : "karigars are"} waiting for your approval.`
                    : "Nobody is waiting on you right now."}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {can("rewards", "create") ? (
                <Button
                  icon={<QrCode aria-hidden="true" className="size-4" />}
                  onClick={() => navigate(`${base}/rewards`)}
                >
                  Issue codes
                </Button>
              ) : null}
              {can("counters", "create") ? (
                <Button
                  icon={<Plus aria-hidden="true" className="size-4" />}
                  onClick={() => navigate(`${base}/counters`)}
                  variant="inverse"
                >
                  Add counter
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        {see.team || see.counters || see.karigars || see.approvals ? (
        <StatGrid>
          {see.team ? (
            <StatCard label="Sales team" value={counts ? counts.salesReps : <Skeleton className="h-8 w-12" />} />
          ) : null}
          {see.counters ? (
            <StatCard label="Counters" value={counts ? counts.counters : <Skeleton className="h-8 w-12" />} />
          ) : null}
          {see.karigars ? (
            <StatCard label="Karigars" value={counts ? counts.karigars : <Skeleton className="h-8 w-12" />} />
          ) : null}
          {see.approvals ? (
            <StatCard
              hint={pendingTotal > 0 ? "waiting for approval" : loaded ? "all caught up" : undefined}
              label="Pending approvals"
              tone={pendingTotal > 0 ? "attention" : "neutral"}
              value={loaded ? pendingTotal : <Skeleton className="h-8 w-12" />}
            />
          ) : null}
        </StatGrid>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-5">
          {see.approvals ? (
          <Card className={see.rewards ? "lg:col-span-3" : "lg:col-span-5"}>
            <CardHeader
              action={
                <Link
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand-ink hover:underline"
                  to={`${base}/approvals`}
                >
                  Review all <ArrowRight aria-hidden="true" className="size-3.5" />
                </Link>
              }
              title="Waiting for approval"
            />
            {!loaded ? (
              <div className="flex flex-col gap-4 px-5 py-5">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-4/5" />
              </div>
            ) : pending && pending.items.length > 0 ? (
              <ul className="divide-y divide-rule/70">
                {pending.items.map((k) => (
                  <li className="flex items-center gap-3 px-5 py-3" key={k.id}>
                    <Avatar name={k.name} src={k.photoUrl} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{k.name}</p>
                      <p className="truncate text-[13px] text-muted">
                        {k.phone ?? "—"}
                        {k.counterName ? ` · ${k.counterName}` : ""}
                      </p>
                    </div>
                    <p className="hidden shrink-0 text-xs text-muted sm:block">{formatDate(k.createdAt)}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState message="New registrations will appear here as they come in." title="All caught up" />
            )}
          </Card>
          ) : null}

          {see.rewards ? (
          <Card className={`flex flex-col ${see.approvals ? "lg:col-span-2" : "lg:col-span-5"}`}>
            <CardHeader
              action={
                <Link
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand-ink hover:underline"
                  to={`${base}/rewards`}
                >
                  Manage <ArrowRight aria-hidden="true" className="size-3.5" />
                </Link>
              }
              title="Reward codes"
            />
            <div className="flex flex-1 flex-col gap-5 px-5 py-5">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.08em] text-muted uppercase">
                  Still unclaimed
                </p>
                {codes ? (
                  <p className="mt-1.5 font-title text-[2rem] leading-none font-semibold tracking-[-0.02em] tabular-nums text-ink">
                    {codes.outstanding.toLocaleString("en-IN")}
                    <span className="ml-1.5 text-base font-medium text-muted">QCoin</span>
                  </p>
                ) : (
                  <Skeleton className="mt-2 h-8 w-32" />
                )}
              </div>

              <div>
                <div className="flex items-baseline justify-between text-[13px]">
                  <span className="text-muted">Scanned</span>
                  <span className="font-medium tabular-nums text-ink">
                    {codes ? `${Math.round(scannedShare * 100)}%` : "—"}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink/[0.07]">
                  <div
                    className="h-full rounded-full bg-brand transition-[width] duration-700"
                    style={{ width: `${Math.max(scannedShare * 100, codes && codes.scanned > 0 ? 2 : 0)}%` }}
                  />
                </div>
              </div>

                <dl className="mt-auto grid grid-cols-2 gap-3 border-t border-rule/70 pt-4 sm:grid-cols-4">
                <Figure label="Batches" value={codes?.activeBatches} />
                  <Figure label="Issued" value={codes?.issued} />
                <Figure label="Scanned" value={codes?.scanned} />
                  <Figure label="Unscanned" value={codes?.unscanned} />
              </dl>
            </div>
          </Card>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {can("performance") ? (
            <Shortcut icon={<TrendingUp className="size-[1.125rem]" />} title="Performance" to={`${base}/performance`} />
          ) : null}
          {can("links") ? (
            <Shortcut icon={<Link2 className="size-[1.125rem]" />} title="Counter links" to={`${base}/links`} />
          ) : null}
          {user?.role === "org_admin" ? (
            <Shortcut
              icon={<Palette className="size-[1.125rem]" />}
              title="Branding"
              to={`${base}/settings/identity`}
            />
          ) : null}
          {can("settlements") ? (
            <Shortcut icon={<HandCoins className="size-[1.125rem]" />} title="Settlements" to={`${base}/settlements`} />
          ) : null}
        </div>

      </PageBody>
    </Page>
  );
}

function Figure({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 font-title text-lg font-semibold tabular-nums text-ink">
        {value === undefined ? "—" : value.toLocaleString("en-IN")}
      </dd>
    </div>
  );
}

function Shortcut({
  to,
  title,
  icon,
}: {
  to: string;
  title: string;
  icon: ReactNode;
}) {
  return (
    <Link
      className="group flex items-center gap-3.5 rounded-lg border-token border-rule bg-raised px-4 py-4 shadow-card transition-[border-color,box-shadow,translate] hover:-translate-y-px hover:border-brand-border"
      to={to}
    >
      <span
        aria-hidden="true"
        className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-light text-brand-ink ring-1 ring-brand-border ring-inset"
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 items-center justify-between gap-2 font-title text-sm font-semibold text-ink">
        {title}
        <ArrowRight
          aria-hidden="true"
          className="size-4 text-muted transition-[translate,color] group-hover:translate-x-0.5 group-hover:text-brand-ink"
        />
      </span>
    </Link>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatDate(at: string | undefined): string {
  if (!at) return "";
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
